import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import Dropdown from '@vtex/styleguide/lib/Dropdown'
import Input from '@vtex/styleguide/lib/Input'
import Button from '@vtex/styleguide/lib/Button'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import BarcodeReader from '@/checkout-instore-legacy/utils/BarcodeReader'
import ModalTitle from '@/checkout-instore-legacy/components/Commons/ModalTitle'
import ModalSubtitle from '@/checkout-instore-legacy/components/Commons/ModalTitle/ModalSubtitle'
import ModalScanAlert from '@/checkout-instore-legacy/components/Commons/Scan/ModalScanAlert'
import { DEFAULT_GIFT_CARD_PROVIDER } from '@/checkout-instore-legacy/constants/CheckoutConstants'
import { handleInputMask } from '@/checkout-instore-legacy/utils/inputUtils'
import { withMediaQuery } from '@/checkout-instore-legacy/utils/mediaQuery'
import mask from '@/checkout-instore-legacy/utils/mask'

class GiftCardCode extends Component {
  constructor(props) {
    super(props)
    this.state = {
      showScanScreen: false,
    }
  }

  getSelectedGiftCardProvider() {
    const { giftCardProviders } = this.props

    const defaultGiftCardProvider =
      giftCardProviders.length > 0
        ? giftCardProviders[0]
        : DEFAULT_GIFT_CARD_PROVIDER

    return this.props.selectedGiftCardProvider
      ? this.props.selectedGiftCardProvider
      : defaultGiftCardProvider
  }

  maskGiftCard = (value) => {
    const { customGiftCardLength, disableGiftCardMask } = getInstoreConfig()

    if (!value) {
      return value
    }

    if (customGiftCardLength > 0 || disableGiftCardMask) {
      return value
    }

    const selectedGiftCardProvider = this.getSelectedGiftCardProvider()
    if (selectedGiftCardProvider !== DEFAULT_GIFT_CARD_PROVIDER) {
      return value
    }

    return mask(value, 'SSSS-SSSS-SSSS-SSSS')
  }

  getCleanRedemptionCode() {
    return this.props.redemptionCode.trim().toUpperCase()
  }

  componentDidUpdate(prevProps) {
    const giftCardInput = this.giftCardInput
    const selectedGiftCardProvider = this.getSelectedGiftCardProvider()
    if (
      giftCardInput &&
      selectedGiftCardProvider === DEFAULT_GIFT_CARD_PROVIDER
    ) {
      handleInputMask(
        prevProps.redemptionCode,
        this.props.redemptionCode,
        this.maskGiftCard,
        giftCardInput
      )
    }
  }

  componentDidMount() {
    BarcodeReader.listen(this.handleBarcodeRead)
    window.handleBarcodeRead = (barcode) =>
      this.handleBarcodeRead({ data: { barcode } }) // used on custom scripts
  }

  componentWillUnmount() {
    BarcodeReader.unlisten(this.handleBarcodeRead)
    window.handleBarcodeRead = null
  }

  handleBarcodeRead = (event) => {
    let { barcode } = event.data

    if (barcode) {
      barcode = barcode.toString()
    }

    this.props.onChange(barcode)
    this.handleFormSubmit()
  }

  handleToggleScanScreen = () => {
    const newShowScanScreen = !this.state.showScanScreen

    if (newShowScanScreen) {
      this.handleOpenCamera()
    }

    this.setState({
      showScanScreen: newShowScanScreen,
    })
  }

  handleOpenCamera() {
    WebViewBridge.sendEvent({
      type: 'openBarcodeCameraVisible',
      params: {
        readText: '^\\w{4}-\\w{4}-\\w{4}-\\w{4}$',
      },
    })
  }

  handleChange = (e) => {
    this.props.onChange(e.target.value)
  }

  handleGiftCardProviderChange = (e) => {
    this.props.onChangeProvider(e.target.value)
  }

  handleFormSubmit = (e) => {
    e && e.preventDefault()
    if (!this.props.redemptionCode) {
      return
    }
    const selectedGiftCardProvider = this.getSelectedGiftCardProvider()
    const redemptionCode = this.getCleanRedemptionCode()
    this.props.onSubmit(redemptionCode, selectedGiftCardProvider)
  }

  render() {
    const redemptionCode = this.getCleanRedemptionCode()
    const { customGiftCardLength } = getInstoreConfig()
    const { showScanScreen } = this.state
    const {
      giftCardProviders,
      isGiftCardLoading,
      intl,
      mediaQuery,
    } = this.props
    const selectedGiftCardProvider = this.getSelectedGiftCardProvider()
    const showGiftCardProviders =
      giftCardProviders && giftCardProviders.length > 1
    const showSideButton =
      mediaQuery({
        phone: false,
        tablet: false,
        desktop: true,
      }) && !showGiftCardProviders

    const giftCardMaxLenght =
      customGiftCardLength > 0
        ? customGiftCardLength
        : selectedGiftCardProvider === DEFAULT_GIFT_CARD_PROVIDER
        ? 19
        : undefined

    return (
      <div>
        <ModalTitle id="giftCardModalTitle" />
        <ModalSubtitle id="giftCardModalSubtitle" />
        {showScanScreen ? (
          <ModalScanAlert onCancel={this.handleToggleScanScreen} />
        ) : (
          <form onSubmit={this.handleFormSubmit}>
            <div className="flex flex-auto items-end tl">
              <div className="flex flex-auto">
                <Input
                  id="redemptionCode"
                  className="f4 f3-ns"
                  type="text"
                  value={this.maskGiftCard(redemptionCode)}
                  onChange={this.handleChange}
                  ref={(giftCardInput) => (this.giftCardInput = giftCardInput)}
                  maxLength={giftCardMaxLenght}
                  label={intl.formatMessage({ id: 'redemptionCodeLabel' })}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
              {showGiftCardProviders && (
                <div className="flex-auto ml5">
                  <Dropdown
                    id="giftCardProvidersSelection"
                    options={giftCardProviders.map((giftCardProvider) => ({
                      value: giftCardProvider,
                      label: giftCardProvider,
                    }))}
                    value={selectedGiftCardProvider}
                    onChange={this.handleGiftCardProviderChange}
                  />
                </div>
              )}
              {showSideButton && (
                <div className="ml4">
                  <Button
                    id="giftCardSubmit"
                    isLoading={isGiftCardLoading}
                    disabled={!redemptionCode}
                    type="submit"
                    variation="primary"
                  >
                    <FormattedMessage id="addPaymentFormAdd" />
                  </Button>
                </div>
              )}
            </div>
            {!showSideButton && (
              <div className="flex justify-end mt5 mt7-l">
                <Button
                  id="giftCardSubmit"
                  isLoading={isGiftCardLoading}
                  disabled={!redemptionCode}
                  type="submit"
                  variation="primary"
                  block
                >
                  <FormattedMessage id="giftCardAddLabel" />
                </Button>
              </div>
            )}
            <div className="tc ma6">
              <FormattedMessage id="orLabel" />
            </div>
            <div className="flex justify-center mt5">
              <Button
                id="giftCardScan"
                type="button"
                variation="secondary"
                onClick={this.handleToggleScanScreen}
                block={!showSideButton}
              >
                <FormattedMessage id="scanCode" />
              </Button>
            </div>
          </form>
        )}
      </div>
    )
  }
}

GiftCardCode.defaultProps = {
  isGiftCardLoading: false,
}

GiftCardCode.propTypes = {
  redemptionCode: PropTypes.string,
  giftCardProviders: PropTypes.arrayOf(PropTypes.string),
  selectedGiftCardProvider: PropTypes.arrayOf(PropTypes.string),
  isGiftCardLoading: PropTypes.bool,
  mediaQuery: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onChangeProvider: PropTypes.func,
  onSubmit: PropTypes.func,
  intl: intlShape.isRequired,
}

export default injectIntl(withMediaQuery(GiftCardCode))
